import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';

const API_DOC_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/crystal-api-guide.md`;
const POSTMAN_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/crystal-media-api.postman_collection.json`;
const PYTHON_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/crystal_api.py`;

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</label>
    {children}
  </div>
);

const SCOPE_DESCRIPTIONS = {
  full: 'Full admin access (everything you can do in this panel).',
  content: 'Content writes only — uploads, collections, films, blog. Cannot touch security/keys/audit.',
  read: 'Read-only — every GET, no writes/deletes.',
};

const ApiAccessPanel = () => {
  const [label, setLabel] = useState('OpenClaw');
  const [days, setDays] = useState(1825);
  const [scope, setScope] = useState('content');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const [keys, setKeys] = useState([]);
  const [keysBusy, setKeysBusy] = useState(false);

  const loadKeys = async () => {
    setKeysBusy(true);
    try { setKeys(await api.request('/api/admin/api-keys')); }
    catch { /* ignore */ }
    finally { setKeysBusy(false); }
  };

  useEffect(() => { loadKeys(); }, []);

  const handleGenerate = async () => {
    setError(''); setCopied(false); setGenerating(true);
    try {
      const res = await api.request('/api/admin/api-key/generate', {
        method: 'POST',
        body: JSON.stringify({ label, days: Number(days) || 1825, scope }),
      });
      setGenerated(res);
      loadKeys();
    } catch (e) {
      setError(e.message || 'Failed to generate key');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!generated?.token) return;
    try {
      await navigator.clipboard.writeText(generated.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* ignore */ }
  };

  const handleRevoke = async (jti) => {
    if (!window.confirm('Revoke this key? Any agent using it will lose access immediately.')) return;
    try {
      await api.request(`/api/admin/api-key/${jti}`, { method: 'DELETE' });
      loadKeys();
    } catch (e) {
      alert(e.message || 'Revoke failed');
    }
  };

  return (
    <div className="space-y-8" data-testid="api-access-panel">
      <div>
        <h2 className="text-2xl font-semibold mb-2">API Access</h2>
        <p className="text-sm text-gray-600 max-w-3xl">
          Generate long-lived API keys to manage your site programmatically — uploading films, photos,
          creating collections, publishing blog posts, and more. All traffic is TLS-encrypted; tokens are
          signed with HMAC-SHA256. Each key has its own scope and can be individually revoked at any time.
        </p>
      </div>

      <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
        <h3 className="text-lg font-medium mb-4">Generate API key</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
          <Field label="Label (for your reference)">
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="OpenClaw"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="api-key-label-input" />
          </Field>
          <Field label="Expires after (days)">
            <input type="number" min="1" max="3650" value={days} onChange={(e) => setDays(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="api-key-days-input" />
          </Field>
          <Field label="Scope">
            <select value={scope} onChange={(e) => setScope(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white" data-testid="api-key-scope-select">
              <option value="content">content — uploads & content</option>
              <option value="read">read — read-only</option>
              <option value="full">full — everything</option>
            </select>
          </Field>
        </div>
        <p className="text-xs text-gray-500 mt-2">{SCOPE_DESCRIPTIONS[scope]}</p>

        <button onClick={handleGenerate} disabled={generating}
          className="mt-4 px-4 py-2 bg-black text-white rounded text-sm hover:bg-gray-800 disabled:opacity-50" data-testid="generate-api-key-btn">
          {generating ? 'Generating…' : 'Generate API key'}
        </button>

        {error && <div className="mt-3 text-sm text-red-600" data-testid="api-key-error">{error}</div>}

        {generated && (
          <div className="mt-6 bg-white border border-gray-200 rounded p-4" data-testid="api-key-result">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <div className="text-xs text-gray-500">Key (copy now — won't be shown again)</div>
                <div className="text-xs text-gray-500 mt-1">
                  Label: <span className="font-mono">{generated.label}</span> · Scope: <span className="font-mono">{generated.scope}</span> · Expires: <span className="font-mono">{generated.expires_at}</span>
                </div>
              </div>
              <button onClick={handleCopy} className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded" data-testid="copy-api-key-btn">
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <textarea readOnly value={generated.token} rows={4}
              className="w-full font-mono text-xs bg-gray-50 border border-gray-200 rounded p-2"
              onFocus={(e) => e.target.select()} data-testid="api-key-value" />
            <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Store this securely. To revoke this key without touching others, use the table below.
            </div>
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium">Existing keys</h3>
          <button onClick={loadKeys} className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded" data-testid="refresh-keys-btn">
            {keysBusy ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Label</th>
                <th className="text-left px-3 py-2">Scope</th>
                <th className="text-left px-3 py-2">Created (UTC)</th>
                <th className="text-left px-3 py-2">Expires (UTC)</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody data-testid="keys-rows">
              {keys.map((k) => (
                <tr key={k.jti} className="border-t border-gray-100">
                  <td className="px-3 py-2">{k.label}</td>
                  <td className="px-3 py-2 font-mono">{k.scope}</td>
                  <td className="px-3 py-2 font-mono">{(k.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-3 py-2 font-mono">{(k.expires_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-3 py-2">
                    {k.revoked
                      ? <span className="text-red-600">revoked</span>
                      : <span className="text-green-700">active</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!k.revoked && (
                      <button onClick={() => handleRevoke(k.jti)} className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50" data-testid={`revoke-key-${k.jti}`}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {keys.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-400">No keys yet — generate one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium mb-1">Guides & downloads</h3>
        <p className="text-sm text-gray-600 mb-4">Share these with your AI agent or import them into your tools.</p>

        <ul className="space-y-3 text-sm">
          <li className="flex items-center justify-between gap-4 border-b border-gray-100 pb-3">
            <div>
              <div className="font-medium">API Guide (Markdown)</div>
              <div className="text-gray-500 text-xs">Complete reference with endpoints, examples, workflows, schema discovery, and security model.</div>
            </div>
            <a href={API_DOC_URL} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-xs" data-testid="download-api-guide-btn">Open</a>
          </li>
          <li className="flex items-center justify-between gap-4 border-b border-gray-100 pb-3">
            <div>
              <div className="font-medium">Postman collection</div>
              <div className="text-gray-500 text-xs">Import in Postman/Bruno; set <code>api_key</code> and go.</div>
            </div>
            <a href={POSTMAN_URL} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-xs" data-testid="download-postman-btn">Download</a>
          </li>
          <li className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium">Python helper (<code>crystal_api.py</code>)</div>
              <div className="text-gray-500 text-xs"><code>python crystal_api.py upload-film --video ... --title ... --with-blog</code></div>
            </div>
            <a href={PYTHON_URL} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-xs" data-testid="download-python-btn">Download</a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ApiAccessPanel;
